/**
 * A porta de entrada do console interno — a tela que o geowork embute em `/admin`.
 *
 * **O que ela deixou de ser.** Até a F6 esta página era um recado: dizia que "o console completo
 * chega na F7" e oferecia dois links. O console existe agora — a carteira, a tela de cada
 * implantação, a liberação de ambiente, a revisão do kickoff e o pacote de Kick Off —, e o recado
 * saiu. O que sobrou aqui é o que uma porta de entrada deve ser: para onde ir, e sob que
 * condições o portal está operando neste instante.
 *
 * **Por que esta tela não repete a carteira.** Ela é a raiz de uma área embutida num iframe do
 * geowork, e a carteira está a um clique. Uma segunda listagem aqui seria um segundo lugar
 * contando a mesma história — e o portal já paga esse preço uma vez, com a etapa derivada, para
 * que o console e o cliente não discordem. Uma cópia a mais é uma chance a mais de divergir.
 *
 * **A custódia da chave mestra abre a tela, e não fica numa aba de diagnóstico.** Quem administra
 * o portal é quem precisa saber que as credenciais dos clientes podem estar protegidas por um
 * arquivo do host em vez do serviço gerenciado que a spec §5.4 promete. Enquanto for esse o caso,
 * este é o primeiro bloco da primeira tela interna.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router";
import { z } from "zod";
import { Aviso, Botao, Cartao, Chip } from "../componentes/ui";
import { CartaoDaCustodia } from "../componentes/cofre";
import { caminhoDaImplantacao, useImplantacoesAguardandoLiberacao } from "../nucleo/console";
import { formatarInstante } from "../nucleo/cofre";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { pedirJson } from "../nucleo/api";
import { caminhoDaRevisao } from "../nucleo/kickoff";

/** A permissão que abre o cadastro do catálogo. Só o administrador interno a tem. */
const PERMISSAO_DO_CATALOGO = "catalogo:gerenciar";
/* Permissão separada da de cima, e não a mesma: um catálogo é o que o comercial VENDE, o
   outro é o que a consultoria PERGUNTA. Quem mantém a lista de Recursos Plus não é
   necessariamente quem escreve as perguntas do kickoff. */
const PERMISSAO_DO_CATALOGO_DO_KICKOFF = "questionario:gerenciar";
const PERMISSAO_DE_VER_INFRA = "infra:ver";
const PERMISSAO_DE_REVISAR_KICKOFF = "kickoff:revisar";

export function ConsoleAdmin() {
  useTitulo("Console interno");
  const sessao = useSessao();
  const podeVerInfra = podeNaSessao(sessao.data, PERMISSAO_DE_VER_INFRA);
  /* A fila de K-O só existe com a K-O no GeoTask (`ativa`); pelo Jira, o console é o de sempre.
     Sem a resposta (carregando, falha ou sem `kickoff:revisar`), o texto não cita sistema algum. */
  const filaDoKickoff = useFilaDoKickoff(podeNaSessao(sessao.data, PERMISSAO_DE_REVISAR_KICKOFF));
  const koNoGeoTask = filaDoKickoff.data?.ativa === true;
  const koNoJira = filaDoKickoff.data?.ativa === false;

  return (
    <>
      <h1 className="titulo-pagina">Console <strong>interno</strong></h1>
      <p className="lead">
        Onde a equipe acompanha cada implantação: a carteira de clientes, a esteira de cada um, a
        liberação do ambiente, a revisão do kickoff, as credenciais guardadas e{" "}
        {koNoGeoTask
          ? "a K-O confirmada no GeoTask."
          : koNoJira
            ? "o resumo que vai para a tarefa de Kick Off no Jira."
            : "o resumo das respostas do Kick Off."}
      </p>

      <CartaoDaCustodia className="mb-6" />

      {podeVerInfra ? <FilaDaInfraestrutura /> : null}
      {koNoGeoTask || filaDoKickoff.isError ? <FilaDoKickoff consulta={filaDoKickoff} /> : null}

      <div className="grid gap-3 md:grid-cols-2 mb-6">
        <Cartao titulo="Implantações">
          <p className="cartao__texto mb-3">
            A carteira, com a etapa atual de cada cliente e o prazo. Daqui se abre a tela de uma
            implantação: esteira, respostas, pendências, cofre e linha do tempo num lugar só.
            Etapa e prazo se atualizam sozinhos conforme a implantação avança.
          </p>
          <Link
            to="/admin/implantacoes"
            className="botao botao--pequeno"
            data-testid="abrir-carteira"
          >
            Abrir a carteira
          </Link>
        </Cartao>
        <Cartao titulo="Nova implantação">
          <p className="cartao__texto mb-3">
            O cadastro do comercial: empresa, contrato, módulos, Recursos Plus, ambiente e a
            pessoa de contato — que recebe o convite de acesso ao salvar.
          </p>
          <Link to="/admin/implantacoes/nova" className="botao botao--pequeno">
            Cadastrar cliente
          </Link>
        </Cartao>
        {/* O cartão só aparece para quem administra o catálogo. Não é proteção — quem nega é o
            serviço —, é não oferecer o que vai voltar 403: um caminho que sempre recusa ensina a
            pessoa a ignorar recusa. O consultor cadastra implantação e não mexe no catálogo. */}
        {podeNaSessao(sessao.data, PERMISSAO_DO_CATALOGO) && (
        <Cartao titulo="Catálogo de Recursos Plus">
          <p className="cartao__texto mb-3">
            O que a área comercial pode vender junto dos módulos. Incluir ou desativar aqui vale na
            hora para o cadastro.
          </p>
          <Link
            to="/admin/catalogo"
            className="botao botao--pequeno"
            data-testid="abrir-catalogo"
          >
            Abrir o catálogo
          </Link>
        </Cartao>
        )}
        {podeNaSessao(sessao.data, PERMISSAO_DO_CATALOGO_DO_KICKOFF) && (
        <Cartao titulo="Catálogo do kickoff">
          <p className="cartao__texto mb-3">
            As perguntas que o cliente responde em cada trilha. Corrigir um texto vale na hora,
            para quem já está preenchendo também; mudar o que a pergunta significa publica uma
            versão nova, e quem começou continua na versão em que entrou.
          </p>
          <Link
            to="/admin/catalogo-do-kickoff"
            className="botao botao--pequeno"
            data-testid="abrir-catalogo-do-kickoff"
          >
            Abrir as perguntas
          </Link>
        </Cartao>
        )}
      </div>

      <h2 className="titulo-secao">O que o console faz por implantação</h2>
      {/* A frase existe porque estes quatro cartões não têm link, e não é descuido: cada um
          desses atos vive DENTRO de uma implantação e não tem endereço sem o id do cliente.
          Sem a frase, a única leitura possível era "descreveram e não levaram a lugar nenhum". */}
      <p className="lead mb-3">
        Cada um destes atos acontece dentro de uma implantação —{" "}
        <Link to="/admin/implantacoes">escolha o cliente na carteira</Link> e eles aparecem na tela
        dele.
      </p>
      <div className="grid gap-3 md:grid-cols-2 mb-6">
        <Cartao titulo="Liberação do ambiente">
          <p className="cartao__texto">
            Registra a liberação do ambiente. O relógio contratual começa quando o kickoff também
            for aceito; até lá, a entrega prevista é &ldquo;a definir&rdquo;. Só quem pode liberar
            ambiente vê o botão, que pede confirmação em dois passos e não se desfaz. Fica na visão
            geral de cada implantação.
          </p>
        </Cartao>
        <Cartao titulo="Revisão do kickoff">
          <p className="cartao__texto">
            As respostas de todas as trilhas, as divergências entre elas e os dois atos do
            consultor: apontar pendência — que <b>devolve a trilha</b> ao cliente com o texto do
            que precisa mudar — e aceitar as respostas depois que todas as trilhas forem enviadas.
          </p>
        </Cartao>
        <Cartao titulo="Cofre de credenciais">
          <p className="cartao__texto">
            As senhas de servidor, banco do ERP e SMTP que o cliente entrega pela trilha de
            infraestrutura, guardadas por implantação. Revelar é exclusivo da equipe de
            infraestrutura e fica registrado com motivo e hora.
          </p>
        </Cartao>
        <Cartao titulo="Resumo do Kick Off">
          <p className="cartao__texto">
            {koNoGeoTask
              ? "O resumo de todas as respostas, para consulta depois de gerar a K-O no GeoTask."
              : koNoJira
                ? "O resumo de todas as respostas, pronto para a tarefa de Kick Off no Jira."
                : "O resumo de todas as respostas do kickoff."}{" "}
            Pergunta sem resposta e trilha não enviada aparecem como lacuna, e o resumo avisa
            quando ficou desatualizado.
          </p>
        </Cartao>
      </div>
    </>
  );
}

const esquemaDaFilaDoKickoff = z.object({
  ativa: z.boolean(),
  itens: z.array(z.object({
    implantacao_id: z.string(),
    organizacao: z.string(),
    kickoff_enviado_em: z.string(),
    consultor_id: z.string().nullable(),
    motivo: z.string().nullable().optional(),
  })),
});
type FilaDoKickoffRecebida = z.infer<typeof esquemaDaFilaDoKickoff>;

function useFilaDoKickoff(habilitada: boolean) {
  return useQuery({
    queryKey: ["console", "pendencias-kickoff"],
    queryFn: ({ signal }) => pedirJson("/implantacoes/pendencias/kickoff", esquemaDaFilaDoKickoff, { signal }),
    enabled: habilitada,
  });
}

function FilaDoKickoff({ consulta }: { consulta: UseQueryResult<FilaDoKickoffRecebida> }) {
  if (consulta.isError) {
    return (
      <Aviso tom="erro" className="mb-6" data-testid="falha-da-fila-do-kickoff">
        Não foi possível carregar as pendências de K-O.{" "}
        <Botao variante="fantasma" tamanho="pequeno" onClick={() => void consulta.refetch()}>
          Tentar de novo
        </Botao>
      </Aviso>
    );
  }
  const itens = consulta.data?.itens ?? [];
  return (
    <Cartao titulo="Gerar tarefa de K-O" className="mb-6" data-testid="fila-do-kickoff"
      acessorio={<Chip estado={itens.length ? "atencao" : "ok"}>{itens.length}</Chip>}>
      {itens.length === 0 ? <p>Nenhum kickoff aguarda geração de tarefa.</p> : (
        <ul className="lista-de-impedimentos">
          {itens.map((item) => <li key={item.implantacao_id}>
            <Link to={caminhoDaRevisao(item.implantacao_id)}>{item.organizacao} — revisar e gerar</Link>
            <div className="celula-secundaria">Cliente concluiu em {formatarInstante(item.kickoff_enviado_em)}</div>
            {item.motivo ? <div className="celula-secundaria">{item.motivo}</div> : null}
          </li>)}
        </ul>
      )}
    </Cartao>
  );
}

function FilaDaInfraestrutura() {
  const fila = useImplantacoesAguardandoLiberacao();

  if (fila.isPending) {
    return (
      <p className="lead mb-6" role="status">
        Conferindo questionários de infraestrutura…
      </p>
    );
  }

  if (fila.isError) {
    return (
      <Aviso tom="erro" className="mb-6" data-testid="falha-da-fila-da-infraestrutura">
        Não foi possível carregar os questionários aguardando liberação agora.
      </Aviso>
    );
  }

  if (fila.data.length === 0) {
    return (
      <Aviso tom="sucesso" className="mb-6" data-testid="fila-da-infraestrutura-vazia">
        Nenhum questionário de infraestrutura aguarda liberação de ambiente.
      </Aviso>
    );
  }

  return (
    <Cartao
      titulo="Questionários de infraestrutura aguardando liberação"
      className="mb-6"
      data-testid="fila-da-infraestrutura"
      acessorio={<Chip estado="atencao">{fila.data.length}</Chip>}
    >
      <p className="cartao__texto mb-3">
        O cliente concluiu esta etapa. Abra a implantação para conferir as respostas e liberar o
        ambiente.
      </p>
      <ul className="lista-de-impedimentos">
        {fila.data.map((item) => (
          <li key={item.id}>
            <Link to={caminhoDaImplantacao(item.id)} data-testid="abrir-aguardando-liberacao">
              {item.organizacao}
            </Link>
            <div className="celula-secundaria numero-tabular">
              Respondido em {formatarInstante(item.respondida_em) ?? "data não registrada"}
            </div>
          </li>
        ))}
      </ul>
    </Cartao>
  );
}
